import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { toast } from '../ui/sonner';
import {
  Clock, Play, Pause, RotateCcw, Eye, Settings, ChevronDown, ChevronUp,
  Loader2, Check, X, AlertTriangle, Zap, Calendar, BarChart3, Timer,
  RefreshCw, Undo2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  scheduled: 'bg-blue-100 text-blue-700',
  manual: 'bg-emerald-100 text-emerald-700',
  dry_run: 'bg-amber-100 text-amber-700',
};

const ScheduledInsertionPanel = ({ onClose, onRefresh }) => {
  const [config, setConfig] = useState(null);
  const [runs, setRuns] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [expandedRun, setExpandedRun] = useState(null);
  const [showSettings, setShowSettings] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [cRes, rRes, mRes] = await Promise.all([
        fetch(`${API}/api/internal-links/scheduler/config`),
        fetch(`${API}/api/internal-links/scheduler/runs?limit=15`),
        fetch(`${API}/api/internal-links/scheduler/metrics`),
      ]);
      if (cRes.ok) setConfig(await cRes.json());
      if (rRes.ok) { const d = await rRes.json(); setRuns(d.runs || []); }
      if (mRes.ok) setMetrics(await mRes.json());
    } catch { toast.error('Failed to load scheduler data'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const saveConfig = async (updates) => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/internal-links/scheduler/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });
      if (res.ok) {
        const d = await res.json();
        setConfig(d.config);
        toast.success('Settings saved');
      } else {
        toast.error('Failed to save');
      }
    } catch { toast.error('Network error'); }
    setSaving(false);
  };

  const triggerRun = async (mode) => {
    setRunning(true);
    try {
      const endpoint = mode === 'dry_run' ? 'dry-run' : 'run-now';
      const res = await fetch(`${API}/api/internal-links/scheduler/${endpoint}`, { method: 'POST' });
      if (res.ok) {
        const d = await res.json();
        toast.success(`${mode === 'dry_run' ? 'Dry run' : 'Manual run'} complete: ${d.links_inserted} links ${mode === 'dry_run' ? 'would be' : ''} inserted in ${d.blogs_updated || d.blogs_eligible} blogs`);
        fetchData();
        onRefresh?.();
      } else {
        toast.error('Run failed');
      }
    } catch { toast.error('Network error'); }
    setRunning(false);
  };

  const rollbackBatch = async (batchId) => {
    if (!window.confirm(`Rollback all insertions from batch ${batchId}? Blog content will revert to pre-insertion state.`)) return;
    try {
      const res = await fetch(`${API}/api/internal-links/scheduler/rollback/${batchId}`, { method: 'POST' });
      if (res.ok) {
        const d = await res.json();
        toast.success(`Rolled back ${d.rolled_back} blogs`);
        fetchData();
        onRefresh?.();
      } else {
        toast.error('Rollback failed');
      }
    } catch { toast.error('Network error'); }
  };

  const formatTime = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
    } catch { return iso; }
  };

  const cadenceLabel = (hours) => {
    if (hours <= 6) return 'Every 6h';
    if (hours <= 12) return 'Every 12h';
    if (hours <= 24) return 'Nightly';
    return 'Weekly';
  };

  if (loading) return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="scheduled-insertion-panel">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-5 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white z-10 rounded-t-2xl">
          <div>
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <Timer className="w-5 h-5 text-blue-600" />
              Scheduled Auto-Insertion
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">Automatic internal link insertion into blog HTML</p>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={fetchData} data-testid="refresh-scheduler-btn">
              <RefreshCw className="w-3.5 h-3.5" />
            </Button>
            <Button size="sm" variant="outline" onClick={onClose} data-testid="close-scheduler-btn">
              <X className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>

        <div className="p-5 space-y-5">
          {/* Status + Quick Actions */}
          <div className="flex flex-wrap items-center gap-3" data-testid="scheduler-status-bar">
            <div className="flex items-center gap-2">
              <div className={`w-2.5 h-2.5 rounded-full ${config?.enabled ? 'bg-emerald-500 animate-pulse' : 'bg-gray-300'}`} />
              <span className="text-sm font-medium">{config?.enabled ? 'Scheduler Active' : 'Scheduler Disabled'}</span>
              <Badge className="text-xs" variant="outline">{cadenceLabel(config?.cadence_hours || 24)}</Badge>
              {config?.schedule_time && <span className="text-xs text-gray-400">at {config.schedule_time} UTC</span>}
            </div>
            <div className="flex-1" />
            <Button size="sm" variant="outline" onClick={() => saveConfig({ enabled: !config?.enabled })} data-testid="toggle-scheduler-btn">
              {config?.enabled ? <Pause className="w-3.5 h-3.5 mr-1" /> : <Play className="w-3.5 h-3.5 mr-1" />}
              {config?.enabled ? 'Disable' : 'Enable'}
            </Button>
            <Button size="sm" variant="outline" onClick={() => triggerRun('dry_run')} disabled={running} data-testid="dry-run-btn">
              {running ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Eye className="w-3.5 h-3.5 mr-1" />}
              Dry Run
            </Button>
            <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={() => triggerRun('manual')} disabled={running} data-testid="run-now-btn">
              {running ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Zap className="w-3.5 h-3.5 mr-1" />}
              Run Now
            </Button>
            <Button size="sm" variant="outline" onClick={() => setShowSettings(!showSettings)} data-testid="settings-toggle-btn">
              <Settings className="w-3.5 h-3.5" />
            </Button>
          </div>

          {/* Last Run Summary */}
          {config?.last_run_at && (
            <div className="p-3 bg-gray-50 rounded-lg text-sm flex items-center gap-4" data-testid="last-run-summary">
              <Clock className="w-4 h-4 text-gray-400" />
              <span className="text-gray-600">Last run: <strong>{formatTime(config.last_run_at)}</strong></span>
              {config.last_run_mode && <Badge className={`text-xs ${statusColors[config.last_run_mode] || 'bg-gray-100'}`}>{config.last_run_mode}</Badge>}
              {config.last_batch_id && <span className="text-xs text-gray-400">Batch: {config.last_batch_id}</span>}
            </div>
          )}

          {/* Settings Panel */}
          {showSettings && config && (
            <Card data-testid="scheduler-settings">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Scheduler Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Cadence</label>
                    <select className="w-full h-9 rounded-md border border-gray-300 px-3 text-sm bg-white"
                      value={config.cadence_hours || 24}
                      onChange={e => saveConfig({ cadence_hours: parseInt(e.target.value) })}
                      data-testid="cadence-select">
                      <option value="6">Every 6 hours</option>
                      <option value="12">Every 12 hours</option>
                      <option value="24">Nightly (24h)</option>
                      <option value="168">Weekly</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Schedule Time (UTC)</label>
                    <Input type="time" value={config.schedule_time || '02:00'}
                      onChange={e => saveConfig({ schedule_time: e.target.value })}
                      data-testid="schedule-time-input" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Max Blogs Per Run</label>
                    <Input type="number" min={1} max={200} value={config.max_blogs_per_run || 50}
                      onChange={e => saveConfig({ max_blogs_per_run: parseInt(e.target.value) || 50 })}
                      data-testid="max-blogs-input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Skip Recent Edits (hours)</label>
                    <Input type="number" min={0} max={168} value={config.skip_recent_manual_edits_hours || 24}
                      onChange={e => saveConfig({ skip_recent_manual_edits_hours: parseInt(e.target.value) || 0 })}
                      data-testid="skip-edits-input" />
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="checkbox" checked={config.only_published ?? true}
                        onChange={e => saveConfig({ only_published: e.target.checked })}
                        className="w-4 h-4 rounded border-gray-300 text-emerald-600"
                        data-testid="only-published-check" />
                      Only published blogs
                    </label>
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="checkbox" checked={config.notify_on_completion ?? true}
                        onChange={e => saveConfig({ notify_on_completion: e.target.checked })}
                        className="w-4 h-4 rounded border-gray-300 text-emerald-600"
                        data-testid="notify-check" />
                      Email on completion
                    </label>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Scheduler Metrics */}
          {metrics && (
            <div className="grid grid-cols-3 md:grid-cols-5 gap-3" data-testid="scheduler-metrics">
              {[
                { label: 'Runs Completed', value: metrics.scheduled_runs_completed, color: 'text-blue-700' },
                { label: 'Links Auto-Inserted', value: metrics.total_links_auto_inserted, color: 'text-emerald-700' },
                { label: 'Blogs Updated', value: metrics.blogs_auto_updated, color: 'text-purple-700' },
                { label: 'Failure Rate', value: `${metrics.failure_rate}%`, color: metrics.failure_rate > 5 ? 'text-red-600' : 'text-green-600' },
                { label: 'Avg Links/Run', value: metrics.avg_links_per_run, color: 'text-blue-600' },
              ].map((m, i) => (
                <div key={m.label || `sched-metric-${i}`} className="text-center p-3 bg-gray-50 rounded-lg" data-testid={`sched-metric-${i}`}>
                  <p className={`text-xl font-bold ${m.color}`}>{m.value}</p>
                  <p className="text-[10px] text-gray-500 mt-0.5">{m.label}</p>
                </div>
              ))}
            </div>
          )}

          {/* Gap Analysis */}
          {metrics && (metrics.blogs_missing_service_links > 0 || metrics.blogs_missing_fdi_links > 0) && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm" data-testid="gap-analysis">
              <p className="font-medium text-amber-800 mb-1">
                <AlertTriangle className="w-4 h-4 inline mr-1" /> Gap Analysis
              </p>
              {metrics.blogs_missing_service_links > 0 && (
                <p className="text-amber-700">{metrics.blogs_missing_service_links} blogs still missing service links</p>
              )}
              {metrics.blogs_missing_fdi_links > 0 && (
                <p className="text-amber-700">{metrics.blogs_missing_fdi_links} of {metrics.fdi_total} FDI blogs missing FDI links</p>
              )}
            </div>
          )}

          {/* Recent Runs */}
          <Card data-testid="recent-runs">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <Calendar className="w-4 h-4 text-blue-600" /> Recent Runs
              </CardTitle>
            </CardHeader>
            <CardContent>
              {runs.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-6">No scheduled runs yet. Click "Run Now" or enable the scheduler.</p>
              ) : (
                <div className="space-y-2">
                  {runs.map((run) => {
                    const isExpanded = expandedRun === run.batch_id;
                    return (
                      <div key={run.batch_id} className="border border-gray-200 rounded-lg overflow-hidden" data-testid={`run-${run.batch_id}`}>
                        <div className="p-3 flex items-center gap-3 cursor-pointer hover:bg-gray-50"
                          onClick={() => setExpandedRun(isExpanded ? null : run.batch_id)}>
                          <Badge className={`text-xs ${statusColors[run.mode] || 'bg-gray-100 text-gray-700'}`}>{run.mode}</Badge>
                          <span className="text-xs text-gray-500">{formatTime(run.started_at)}</span>
                          <div className="flex-1 flex items-center gap-4 text-xs text-gray-600">
                            <span>{run.blogs_updated || 0} blogs</span>
                            <span className="text-emerald-600 font-medium">{run.links_inserted || 0} links</span>
                            {run.failures > 0 && <span className="text-red-600">{run.failures} failed</span>}
                            {run.skipped_count > 0 && <span className="text-gray-400">{run.skipped_count} skipped</span>}
                          </div>
                          {run.rollback_status === 'rolled_back' && (
                            <Badge className="bg-amber-100 text-amber-700 text-xs">Rolled Back</Badge>
                          )}
                          {run.mode !== 'dry_run' && !run.rollback_status && run.blogs_updated > 0 && (
                            <Button size="sm" variant="ghost" className="text-xs text-amber-600 hover:bg-amber-50 h-7 px-2"
                              onClick={(e) => { e.stopPropagation(); rollbackBatch(run.batch_id); }}
                              data-testid={`rollback-${run.batch_id}`}>
                              <Undo2 className="w-3 h-3 mr-1" /> Rollback
                            </Button>
                          )}
                          {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                        </div>

                        {isExpanded && (
                          <div className="p-3 border-t border-gray-100 bg-gray-50/50 space-y-3" data-testid={`run-detail-${run.batch_id}`}>
                            <div className="grid grid-cols-3 md:grid-cols-6 gap-2 text-center text-xs">
                              <div><p className="font-bold text-gray-700">{run.blogs_scanned}</p><p className="text-gray-400">Scanned</p></div>
                              <div><p className="font-bold text-blue-600">{run.blogs_eligible}</p><p className="text-gray-400">Eligible</p></div>
                              <div><p className="font-bold text-emerald-600">{run.blogs_updated}</p><p className="text-gray-400">Updated</p></div>
                              <div><p className="font-bold text-purple-600">{run.links_inserted}</p><p className="text-gray-400">Links</p></div>
                              <div><p className="font-bold text-red-600">{run.failures}</p><p className="text-gray-400">Failures</p></div>
                              <div><p className="font-bold text-gray-500">{run.skipped_count}</p><p className="text-gray-400">Skipped</p></div>
                            </div>

                            {/* Results */}
                            {run.results?.length > 0 && (
                              <div>
                                <p className="text-xs font-medium text-gray-600 mb-1">Blog Results:</p>
                                <div className="max-h-32 overflow-y-auto space-y-1">
                                  {run.results.map((r, i) => (
                                    <div key={lk.anchor_text || `inserted-${i}`} className="flex items-center gap-2 text-xs px-2 py-1 bg-white rounded">
                                      {r.status === 'success' ? <Check className="w-3 h-3 text-emerald-500" /> : <X className="w-3 h-3 text-gray-400" />}
                                      <span className="text-gray-700 truncate flex-1">{r.title || r.slug}</span>
                                      <span className="text-emerald-600 font-medium">{r.inserted} ins</span>
                                      <span className="text-gray-400">{r.skipped} skip</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Failures */}
                            {run.failure_details?.length > 0 && (
                              <div>
                                <p className="text-xs font-medium text-red-600 mb-1">Failures:</p>
                                <div className="space-y-1">
                                  {run.failure_details.map((f, i) => (
                                    <div key={`skipped-${i}`} className="text-xs px-2 py-1 bg-red-50 rounded text-red-700">
                                      {f.title || f.slug}: {f.error}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Skipped */}
                            {run.skipped_details?.length > 0 && (
                              <div>
                                <p className="text-xs font-medium text-gray-500 mb-1">Skipped Blogs:</p>
                                <div className="max-h-24 overflow-y-auto space-y-1">
                                  {run.skipped_details.map((s, i) => (
                                    <div key={lk.anchor_text || `pending-${i}`} className="flex items-center gap-2 text-xs px-2 py-1 bg-white rounded">
                                      <span className="text-gray-600 truncate flex-1">{s.title || s.slug}</span>
                                      <Badge variant="outline" className="text-[10px]">{s.reason?.replace(/_/g, ' ')}</Badge>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ScheduledInsertionPanel;
